import React, { useState, useEffect } from "react";
import QRCode from "qrcode";
import * as url from "helper/UrlHelper";

const PaymentModal = ({ invoiceid, amount, isPaid, isSender, onClose }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  useEffect(() => {
    // Generate QR code when modal opens
    const generateQRCode = async () => {
      if (invoiceid) {
        const paymentUrl = url.FRONTEND_BASE_URL + `/quickpay/${invoiceid}`;
        try {
          const qrUrl = await QRCode.toDataURL(paymentUrl);
          setQrCodeUrl(qrUrl);
        } catch (error) {
          console.error("Error generating QR code:", error);
        }
      }
    };
    
    generateQRCode();
  }, [invoiceid]);

  const handleCopyUrl = () => {
    const paymentUrl = url.FRONTEND_BASE_URL + `/quickpay/${invoiceid}`;
    navigator.clipboard.writeText(paymentUrl).then(() => {
      alert("Payment URL copied to clipboard!");
    }).catch((err) => {
      console.error("Failed to copy URL:", err);
    });
  };

  const handleDownloadQR = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `payment-qr-${invoiceid}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePayNow = () => {
    const paymentUrl = url.FRONTEND_BASE_URL + `/quickpay/${invoiceid}`;
    window.open(paymentUrl, '_blank');
  };

  const getModalTitle = () => {
    if (isSender) {
      return isPaid ? "Payment Completed" : "Payment Request Sent";
    }
    return isPaid ? "Payment Received" : "Payment Required";
  };

  const getModalIcon = () => {
    if (isPaid) {
      return "fas fa-check-circle text-success";
    }
    return isSender ? "fas fa-paper-plane" : "fas fa-credit-card";
  };

  const getPaymentStatus = () => {
    if (isPaid) {
      return {
        text: "Payment Completed",
        class: "text-success",
        bgClass: "bg-success"
      };
    }
    return {
      text: isSender ? "Awaiting Payment" : "Payment Required",
      class: isSender ? "text-warning" : "text-danger",
      bgClass: isSender ? "bg-warning" : "bg-danger"
    };
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header bg-light border-0 pb-2">
            <h5 className="modal-title d-flex align-items-center">
              <i className={`${getModalIcon()} me-2`}></i>
              {getModalTitle()}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body px-4 py-4">
            <div className="text-center mb-4">
              <div className="bg-light p-4 rounded-3 mb-3 position-relative">
                <div className={`badge ${getPaymentStatus().bgClass} position-absolute top-0 start-50 translate-middle px-3 py-2`}>
                  <small className="text-white fw-bold">{getPaymentStatus().text}</small>
                </div>
                <h6 className="text-muted mb-2 mt-3">
                  {isSender ? "Payment Amount" : "Amount Due"}
                </h6>
                <div className="h2 text-primary mb-1 fw-bold">
                  <span style={{ fontSize: '1.5rem' }}>€</span>
                  {amount}
                </div>
              </div>
            </div>

            {/* Receiver View - Show QR and payment options when not paid */}
            {!isSender && !isPaid && (
              <div className="border-top pt-4">
                <h6 className="mb-3 text-muted">Payment Options:</h6>
                

                {qrCodeUrl && (
                  <div className="text-center">
                    <h6 className="mb-3 text-muted">Scan QR Code to Pay:</h6>
                    <div className="d-inline-block p-3 bg-white border rounded-3 shadow-sm">
                      <img
                        src={qrCodeUrl}
                        alt="Payment QR Code"
                        style={{ width: "180px", height: "180px" }}
                        className="rounded"
                      />
                    </div>
                    <p className="text-muted mt-2 small">Scan this QR code to open payment page</p>
                  
                  </div>
                )}
                
                <div className="alert alert-info d-flex align-items-center mt-3 mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  <small>Click "Proceed to Payment" to complete your payment securely.</small>
                </div>
              </div>
            )}

            {/* Sender View - Show status only */}
            {isSender && !isPaid && (
              <div className="border-top pt-4">
                <div className="alert alert-warning d-flex align-items-center mb-0">
                  <i className="fas fa-clock me-2"></i>
                  <small>Payment request has been sent. Waiting for payment to be completed.</small>
                </div>
              </div>
            )}

            {/* Payment completed message for receiver */}
            {!isSender && isPaid && (
              <div className="border-top pt-4">
                <div className="alert alert-success d-flex align-items-center mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  <small>Payment has been completed successfully.</small>
                </div>
              </div>
            )}

            {/* Payment completed message for sender */}
            {isSender && isPaid && (
              <div className="border-top pt-4">
                <div className="alert alert-success d-flex align-items-center mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  <small>Payment has been received successfully.</small>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer bg-light border-0 pt-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              {isPaid ? "Close" : "Cancel"}
            </button>
            
            {/* Show payment button only for receiver and when not paid */}
            {!isSender && !isPaid && (
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={handlePayNow}
              >
                <i className="fas fa-credit-card me-2"></i>
                Proceed to Payment
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
